/**
 * Static collision world: an array of axis-aligned boxes plus a uniform grid
 * broadphase over the XZ plane.
 *
 * Why AABBs rather than the capsule-vs-mesh setup you would reach for in a
 * normal FPS:
 *
 *  - The server has to run this too, inside a Durable Object. Pulling in a
 *    WASM physics engine (Rapier et al.) would mean shipping it to the Worker
 *    as well, and cross-build float determinism becomes a live risk.
 *  - Prop hunt hulls are boxes anyway. A player *is* a crate.
 *  - Axis-separated AABB resolution gives wall sliding for free and is short
 *    enough to reason about line by line, which matters a great deal when a
 *    one-ULP disagreement between client and server shows up as rubber-banding.
 *
 * Collision geometry is authored as data (see mapfmt.ts). It is never derived
 * from mesh geometry at runtime — the server has no GLB loader and never will.
 */

import { WORLD_MIN_XZ, WORLD_MAX_XZ } from "./rules.js";

export interface Aabb {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

/** Build an AABB from a centre point and full extents. */
export function boxFromCenter(
  cx: number, cy: number, cz: number,
  width: number, height: number, depth: number,
): Aabb {
  const hw = width * 0.5;
  const hd = depth * 0.5;
  return {
    minX: cx - hw, minY: cy, minZ: cz - hd,
    maxX: cx + hw, maxY: cy + height, maxZ: cz + hd,
  };
}

export function aabbOverlaps(a: Aabb, b: Aabb): boolean {
  return (
    a.minX < b.maxX && a.maxX > b.minX &&
    a.minY < b.maxY && a.maxY > b.minY &&
    a.minZ < b.maxZ && a.maxZ > b.minZ
  );
}

const CELL_SIZE = 4;

export class StaticWorld {
  readonly solids: readonly Aabb[];

  private readonly cellSize: number;
  private readonly gridW: number;
  private readonly gridD: number;
  private readonly cells: number[][];

  /** Scratch buffers, reused to keep `query` allocation-free on the hot path. */
  private readonly seenStamp: Int32Array;
  private stamp = 0;

  /**
   * Solids that are present in the array but must not collide.
   *
   * This is how a possessed prop leaves the world (see possession.ts): a hider
   * wearing a crate carries its hull with them, so the crate's static box has
   * to stop existing or they walk around leaving their own body behind as an
   * obstacle — including one they immediately collide with themselves.
   *
   * A mask rather than a rebuild for two reasons. Rebuilding reallocates the
   * broadphase grid on an event that happens mid-round, and — the real one —
   * indices into `solids` are a *contract* (`mapfmt.mapSolids` fixes the order,
   * `possession.propSolidIndex` addresses one by number, the client's render
   * batching lines up against the same order). Compacting the array would
   * renumber every solid after the suppressed one, and everything holding an
   * index would silently mean a different box.
   *
   * `null` until something is actually suppressed, so worlds that never see a
   * possession — every test world, every M0/M1 path — pay nothing for this.
   */
  private suppressed: Uint8Array | null = null;

  constructor(solids: readonly Aabb[], cellSize: number = CELL_SIZE) {
    this.solids = solids;
    this.cellSize = cellSize;

    const span = WORLD_MAX_XZ - WORLD_MIN_XZ;
    this.gridW = Math.ceil(span / cellSize);
    this.gridD = this.gridW;
    this.cells = Array.from({ length: this.gridW * this.gridD }, () => [] as number[]);
    this.seenStamp = new Int32Array(solids.length);

    for (let i = 0; i < solids.length; i++) {
      const s = solids[i]!;
      const x0 = this.cellIndex(s.minX);
      const x1 = this.cellIndex(s.maxX);
      const z0 = this.cellIndex(s.minZ);
      const z1 = this.cellIndex(s.maxZ);
      for (let cz = z0; cz <= z1; cz++) {
        for (let cx = x0; cx <= x1; cx++) {
          this.cells[cz * this.gridW + cx]!.push(i);
        }
      }
    }
  }

  private cellIndex(coord: number): number {
    const i = Math.floor((coord - WORLD_MIN_XZ) / this.cellSize);
    return i < 0 ? 0 : i >= this.gridW ? this.gridW - 1 : i;
  }

  /**
   * Take a solid out of collision, or put it back.
   *
   * Both sides must call this for the same indices or they simulate different
   * worlds — which is a divergence that does *not* converge, unlike a
   * positional one. It is driven from the possessed-prop set, which the server
   * broadcasts whole rather than as a delta, so a client that missed a message
   * cannot end up permanently one prop out of step.
   */
  setSuppressed(index: number, value: boolean): void {
    if (index < 0 || index >= this.solids.length) return;
    if (this.suppressed === null) {
      if (!value) return;
      this.suppressed = new Uint8Array(this.solids.length);
    }
    this.suppressed[index] = value ? 1 : 0;
  }

  isSuppressed(index: number): boolean {
    return this.suppressed !== null && this.suppressed[index] === 1;
  }

  /** Drop every suppression. Used when a round resets the possessed set. */
  clearSuppressed(): void {
    this.suppressed?.fill(0);
  }

  /**
   * Collect indices of solids whose cells overlap the given box.
   *
   * Returns indices in ascending order. The sort is not for speed — it
   * guarantees that collision resolution visits solids in the same order on
   * every machine regardless of how the grid was populated, which is a
   * precondition for client and server agreeing on the outcome.
   */
  query(
    minX: number, minZ: number,
    maxX: number, maxZ: number,
    out: number[],
  ): number[] {
    out.length = 0;
    const marker = ++this.stamp;
    const suppressed = this.suppressed;

    const x0 = this.cellIndex(minX);
    const x1 = this.cellIndex(maxX);
    const z0 = this.cellIndex(minZ);
    const z1 = this.cellIndex(maxZ);

    for (let cz = z0; cz <= z1; cz++) {
      const row = cz * this.gridW;
      for (let cx = x0; cx <= x1; cx++) {
        const bucket = this.cells[row + cx]!;
        for (let k = 0; k < bucket.length; k++) {
          const idx = bucket[k]!;
          if (this.seenStamp[idx] === marker) continue;
          this.seenStamp[idx] = marker;
          // Filtered here rather than in the three movement axes and in
          // `hullFits`, so there is exactly one place that can forget.
          if (suppressed !== null && suppressed[idx] === 1) continue;
          out.push(idx);
        }
      }
    }

    out.sort(numericAscending);
    return out;
  }
}

function numericAscending(a: number, b: number): number {
  return a - b;
}

/**
 * The M0 test world: a floor slab and four perimeter walls, nothing else.
 *
 * Deliberately featureless. M0 exists to prove prediction, reconciliation and
 * interpolation are correct; adding geometry before that is proven just makes
 * netcode bugs harder to attribute.
 */
export function createFlatTestWorld(): StaticWorld {
  const lo = WORLD_MIN_XZ;
  const hi = WORLD_MAX_XZ;
  const wallHeight = 8;
  const wallThickness = 2;

  const solids: Aabb[] = [
    // Floor — a real solid, not a special-cased ground plane, so the same
    // collision path handles it as handles everything else.
    { minX: lo, minY: -1, minZ: lo, maxX: hi, maxY: 0, maxZ: hi },
    // Perimeter
    { minX: lo, minY: 0, minZ: lo, maxX: hi, maxY: wallHeight, maxZ: lo + wallThickness },
    { minX: lo, minY: 0, minZ: hi - wallThickness, maxX: hi, maxY: wallHeight, maxZ: hi },
    { minX: lo, minY: 0, minZ: lo, maxX: lo + wallThickness, maxY: wallHeight, maxZ: hi },
    { minX: hi - wallThickness, minY: 0, minZ: lo, maxX: hi, maxY: wallHeight, maxZ: hi },
  ];

  // A few scattered blocks: without something to slide along, a collision bug
  // that only manifests during contact cannot be seen at all.
  //
  // The area around the origin is deliberately left clear — it is the spawn
  // point, and it is where the tests place a player.
  const blocks: [number, number, number, number, number][] = [
    // [centreX, centreZ, width, height, depth]
    [-20, -20, 6, 4, 6],
    [20, -20, 4, 2, 12],
    [-20, 20, 12, 3, 4],
    [20, 20, 6, 4, 6],
    [0, -34, 20, 5, 3],
    [-38, 6, 3, 6, 20],
    [38, -6, 3, 6, 20],
  ];
  for (const [cx, cz, w, h, d] of blocks) {
    solids.push(boxFromCenter(cx, 0, cz, w, h, d));
  }

  return new StaticWorld(solids);
}

/**
 * A floor and nothing else.
 *
 * Movement-feel assertions (top speed, friction, diagonal normalization) need
 * room to run without incidentally colliding with level geometry. Using the
 * furnished test world for those silently measures the wall the player ran
 * into rather than the movement model — which is worse than having no test,
 * because it still goes green when the movement model breaks.
 */
export function createOpenTestWorld(): StaticWorld {
  return new StaticWorld([
    {
      minX: WORLD_MIN_XZ, minY: -1, minZ: WORLD_MIN_XZ,
      maxX: WORLD_MAX_XZ, maxY: 0, maxZ: WORLD_MAX_XZ,
    },
  ]);
}
